import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { gsap, ScrollTrigger } from "../../utils/gsapSetup";
import { fetchActiveCategories, fetchTopVideos } from "../../services/firestore";
import { resolveByLangSuffix, getCategorySlug } from "../../utils/i18nHelpers";

const serviceIcons = {
    video: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
        </svg>
    ),
    photo: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
        </svg>
    ),
    social: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
    ),
    drone: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M12 12m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
            <path d="M4 4l4 4" /><path d="M20 4l-4 4" /><path d="M4 20l4-4" /><path d="M20 20l-4-4" />
            <circle cx="4" cy="4" r="2" /><circle cx="20" cy="4" r="2" />
            <circle cx="4" cy="20" r="2" /><circle cx="20" cy="20" r="2" />
        </svg>
    ),
    edit: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
    ),
    globe: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
    ),
    music: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
        </svg>
    ),
    mic: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
        </svg>
    ),
    star: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
    ),
    pen: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><path d="M2 2l7.586 7.586" /><circle cx="11" cy="11" r="2" />
        </svg>
    ),
    building: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
            <path d="M9 22v-4h6v4" /><line x1="8" y1="6" x2="8" y2="6" /><line x1="12" y1="6" x2="12" y2="6" /><line x1="16" y1="6" x2="16" y2="6" />
            <line x1="8" y1="10" x2="8" y2="10" /><line x1="12" y1="10" x2="12" y2="10" /><line x1="16" y1="10" x2="16" y2="10" />
            <line x1="8" y1="14" x2="8" y2="14" /><line x1="12" y1="14" x2="12" y2="14" /><line x1="16" y1="14" x2="16" y2="14" />
        </svg>
    ),
    heart: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
    ),
};

// Fallback icon
const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" />
    </svg>
);

const Services = () => {
    const { t, i18n } = useTranslation();
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        Promise.all([fetchActiveCategories(), fetchTopVideos()])
            .then(([cats, topVideos]) => {
                const catsWithVideos = new Set(
                    topVideos.map((v) => v.category_id || v.category?.id)
                );
                setCategories(cats.filter((c) => catsWithVideos.has(c.id)));
            })
            .catch((err) => console.error("Hizmetler yüklenemedi:", err));
    }, []);

    useEffect(() => {
        if (!sectionRef.current || categories.length === 0) return;

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: sectionRef.current,
                start: "top 75%",
                toggleActions: "play none none reverse",
            },
        });

        tl.fromTo(
            sectionRef.current.querySelectorAll(".services-title"),
            { opacity: 0, y: -30 },
            { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" }
        );

        cardsRef.current.forEach((card, i) => {
            if (!card) return;
            tl.fromTo(
                card,
                { opacity: 0, y: 40 },
                { opacity: 1, y: 0, duration: 0.5, ease: "power3.out" },
                i === 0 ? "-=0.3" : "-=0.35"
            );
        });

        return () => {
            tl.scrollTrigger?.kill();
            tl.kill();
        };
    }, [categories]);

    const isRtl = i18n.language === "ar";

    return (
        <section
            ref={sectionRef}
            className="bg-black py-24 px-4"
            dir={isRtl ? "rtl" : "ltr"}
        >
            <div className="max-w-6xl mx-auto">
                <h2 className="services-title text-white text-3xl lg:text-5xl font-bold text-center mb-4 tracking-wide uppercase opacity-0">
                    {t("services.title")}
                </h2>
                <p className="services-title text-gray-400 text-center mb-16 text-base lg:text-lg opacity-0">
                    {t("services.subtitle")}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categories.map((cat, i) => (
                        <Link
                            key={cat.id}
                            to={`/projects/${getCategorySlug(cat, "tr")}`}
                            ref={(el) => (cardsRef.current[i] = el)}
                            className="opacity-0 group border border-white/10 rounded-2xl p-8 hover:border-white/40 hover:bg-white/5 transition-all duration-300 flex flex-col gap-4 break-words"
                        >
                            <div className="text-white/70 group-hover:text-white transition-colors duration-300">
                                {serviceIcons[cat.icon] || defaultIcon}
                            </div>
                            <h3 className="text-white text-xl font-semibold">
                                {resolveByLangSuffix(cat, "name")}
                            </h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                {resolveByLangSuffix(cat, "desc")}
                            </p>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Services;
